import { Add, Delete, Edit } from "@mui/icons-material"
import { Box, Stack, Tooltip, Typography } from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"
import type {
  BiomeshEventResponse,
  BiomeshSensorResponse,
  EventStatus,
  OperationalStatus,
  SensorFunction,
  SensorType,
} from "@phc/common/gen/threatAnalyticsGen"
import {
  useListEventsBiomeshEventListGet,
  useListPlatformsBiomeshPlatformListGet,
  useListSensorsBiomeshSensorListGet,
} from "@phc/common/gen/threatAnalyticsGen"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import Button from "../Shared/Button"
import {
  eventStatusLabels,
  operationalStatusLabels,
  sensorFunctionLabels,
  sensorTypeLabels,
} from "./copUtils"
import { DeleteEventDialog } from "./DeleteEventDialog"
import { DeleteSensorDialog } from "./DeleteSensorDialog"
import { EventFormDialog } from "./EventFormDialog"
import { SensorFormDialog } from "./SensorFormDialog"

export const SensorList: FC = () => {
  const { data: sensors, isLoading } = useListSensorsBiomeshSensorListGet({
    // TODO: implement pagination in the API and remove this limit
    limit: 10000,
    include: "contacts",
  })
  const { data: nodes } = useListPlatformsBiomeshPlatformListGet()
  const { data: allEvents } = useListEventsBiomeshEventListGet({
    limit: 10000,
  })

  const [formOpen, setFormOpen] = useState(false)
  const [editSensor, setEditSensor] = useState<
    BiomeshSensorResponse | undefined
  >()
  const [deleteSensor, setDeleteSensor] = useState<
    BiomeshSensorResponse | undefined
  >()

  const nodeMap = useMemo(() => {
    return new Map(nodes?.map(p => [p.platform_id, p.name]))
  }, [nodes])

  const eventCountMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const evt of allEvents ?? []) {
      map.set(evt.sensor_id, (map.get(evt.sensor_id) ?? 0) + 1)
    }
    return map
  }, [allEvents])

  const handleAdd = useCallback(() => {
    setEditSensor(undefined)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((sensor: BiomeshSensorResponse) => {
    setEditSensor(sensor)
    setFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setFormOpen(false)
    setEditSensor(undefined)
  }, [])

  const columns: GridColDef<BiomeshSensorResponse>[] = useMemo(
    () => [
      { field: "sensor_name", headerName: "Sensor Name", flex: 1 },
      {
        field: "sensor_type",
        headerName: "Type",
        flex: 0.8,
        valueFormatter: (value: SensorType) => sensorTypeLabels[value],
      },
      {
        field: "sensor_function",
        headerName: "Function",
        flex: 0.8,
        valueFormatter: (value: SensorFunction | null) =>
          value ? sensorFunctionLabels[value] : "—",
      },
      {
        field: "operational_status",
        headerName: "Operational Status",
        flex: 0.9,
        valueFormatter: (value: OperationalStatus) =>
          operationalStatusLabels[value],
      },
      {
        field: "platform_id",
        headerName: "Node",
        flex: 0.8,
        valueFormatter: (value: string | null) =>
          value ? (nodeMap.get(value) ?? "—") : "—",
      },
      {
        field: "contacts",
        headerName: "Contacts",
        flex: 0.8,
        valueGetter: (_value: unknown, row: BiomeshSensorResponse) =>
          row.contacts?.map(c => `${c.first_name} ${c.last_name}`).join(", ") ||
          "—",
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.7,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : "—",
      },
      {
        field: "event_count",
        headerName: "Events",
        flex: 0.5,
        valueGetter: (_value: unknown, row: BiomeshSensorResponse) =>
          eventCountMap.get(row.sensor_id) ?? 0,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        disableExport: true,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setDeleteSensor(row)}
          />,
        ],
      },
    ],
    [handleEdit, nodeMap, eventCountMap]
  )

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Sensor
          </Button>
        </Stack>
        <BaseGrid
          rows={sensors ?? []}
          columns={columns}
          getRowId={row => row.sensor_id}
          loading={isLoading}
          search
          showToolbar
          exported
          exportFileName="biomesh-sensors"
          placeholder="Search sensors..."
          getDetailPanelContent={({ row }) => <SensorEventPanel sensor={row} />}
          getDetailPanelHeight={() => "auto"}
        />
      </Stack>
      <SensorFormDialog
        key={editSensor?.sensor_id ?? "new"}
        open={formOpen}
        onClose={handleCloseForm}
        sensor={editSensor}
      />
      {deleteSensor && (
        <DeleteSensorDialog
          open
          onClose={() => setDeleteSensor(undefined)}
          sensor={deleteSensor}
        />
      )}
    </>
  )
}

/* ── Sensor event detail panel ── */

function TooltipCell({
  params,
}: {
  params: GridRenderCellParams<BiomeshEventResponse>
}) {
  const text = (params.value as string | null) ?? ""
  return (
    <Tooltip title={text} enterDelay={300}>
      <Typography
        variant="body2"
        noWrap
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {text || "—"}
      </Typography>
    </Tooltip>
  )
}

const SensorEventPanel: FC<{ sensor: BiomeshSensorResponse }> = ({
  sensor,
}) => {
  const { data: events, isLoading } = useListEventsBiomeshEventListGet({
    sensor_id: sensor.sensor_id,
    limit: 1000,
  })

  const [addOpen, setAddOpen] = useState(false)
  const [editEvent, setEditEvent] = useState<BiomeshEventResponse | undefined>()
  const [deleteEvent, setDeleteEvent] = useState<
    BiomeshEventResponse | undefined
  >()

  const eventColumns: GridColDef<BiomeshEventResponse>[] = useMemo(
    () => [
      { field: "name", headerName: "Event Name", flex: 1, minWidth: 120 },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 100,
        valueFormatter: (value: EventStatus | null) =>
          value ? eventStatusLabels[value] : "—",
      },
      {
        field: "observations",
        headerName: "Observations",
        flex: 1.5,
        minWidth: 150,
        renderCell: params => <TooltipCell params={params} />,
      },
      {
        field: "resources",
        headerName: "Resources",
        flex: 1,
        minWidth: 120,
        renderCell: params => <TooltipCell params={params} />,
      },
      {
        field: "created_at",
        headerName: "Created",
        flex: 0.6,
        minWidth: 100,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : "—",
      },
      {
        field: "updated_at",
        headerName: "Updated",
        flex: 0.6,
        minWidth: 100,
        valueFormatter: (value: string | null, row: BiomeshEventResponse) =>
          value && value !== row.created_at
            ? new Date(value).toLocaleDateString()
            : "—",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => setEditEvent(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setDeleteEvent(row)}
          />,
        ],
      },
    ],
    []
  )

  return (
    <Box sx={{ p: 2, pl: 6 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="body2Bold">
          Events for {sensor.sensor_name}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Add />}
          onClick={() => setAddOpen(true)}
        >
          Add Event
        </Button>
      </Stack>
      <BaseGrid
        rows={events ?? []}
        columns={eventColumns}
        getRowId={row => row.event_id}
        loading={isLoading}
        checkbox={false}
        search={false}
        sx={{ minHeight: "auto" }}
      />
      {addOpen && (
        <EventFormDialog
          open
          onClose={() => setAddOpen(false)}
          sensorId={sensor.sensor_id}
        />
      )}
      {editEvent && (
        <EventFormDialog
          key={editEvent.event_id}
          open
          onClose={() => setEditEvent(undefined)}
          sensorId={sensor.sensor_id}
          event={editEvent}
        />
      )}
      {deleteEvent && (
        <DeleteEventDialog
          open
          onClose={() => setDeleteEvent(undefined)}
          event={deleteEvent}
        />
      )}
    </Box>
  )
}
